import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import { useAuthMe, useUpdateProfile } from "@/api/queries/auth";
import { useActiveLanguages } from "@/i18n/languageRegistry";
import { cx } from "@/lib/utils";

/**
 * Compact language switcher — shows the active language code and
 * opens a dropdown to switch to any other enabled language.
 *
 *   /cs/quote/123  →  /en/quote/123
 *
 * The language set is the runtime :mod:`languageRegistry` (DB-backed via
 * the branding payload), so an admin-added locale appears here without a
 * code change.
 *
 * ``variant="row"`` renders a labeled segmented row instead of the
 * dropdown — that's the shape the account menu needs, where a nested
 * popup inside a popup would be both fiddly to dismiss and visually
 * wrong next to the theme control it sits under.
 *
 * 🪤 **Volba se od KAN-661 zapisuje i NA ÚČET** (``preferred_lang``).
 * Do té chvíle přepínač měnil jen URL, takže volba nepřežila ani zavření
 * karty — a ``preferred_lang``, ze kterého se posílají transakční e-maily,
 * si dál drželo, co uživatel vyplnil při registraci. Člověk, který si celou
 * administraci přepnul do angličtiny, tedy dostával české e-maily a nikde
 * nebylo vidět proč.
 *
 * Co se NEMĚNÍ: co se právě vykresluje, pořád rozhoduje ``/:lang`` v URL.
 * Účtová hodnota ji nepřebíjí — jazyk je součástí adresy (SEO, sdílené
 * odkazy) a přesměrovat sdílený odkaz podle toho, kdo je zrovna přihlášený,
 * by rozbilo obojí.
 */
interface LangSwitcherProps {
  variant?: "button" | "row";
  /** Fires after a language is picked — lets a host menu close itself. */
  onSwitch?: () => void;
}

export function LangSwitcher({
  variant = "button",
  onSwitch,
}: LangSwitcherProps = {}): JSX.Element {
  const { t } = useTranslation("common");
  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const languages = useActiveLanguages();
  // ``enabled: false`` — čte se z cache, nikdy se nefetchuje. Přepínač
  // visí i v hlavičce marketingu, kde anonymní návštěvník žádné
  // ``/auth/me`` nemá; vlastní dotaz by mu přidal request tam, kde
  // předtím žádný nebyl. Prázdná cache = nikam se nezapisuje, což je
  // pro anonymního správně.
  const me = useAuthMe({ enabled: false });
  const updateProfile = useUpdateProfile();
  const accountLang = me.data?.preferred_lang;

  const switchTo = useCallback(
    (next: string) => {
      const segments = location.pathname.split("/");
      segments[1] = next;
      const newPath = segments.join("/") || "/";
      navigate(newPath + location.search + location.hash);
      // Přihlášený → ulož na účet, ať volba jde s ním na další zařízení
      // a ať z ní žijí i transakční e-maily. Shoda se přeskakuje, aby
      // klik na už aktivní jazyk neposílal PATCH.
      if (me.data && accountLang !== next) {
        updateProfile.mutate({ preferred_lang: next });
      }
      setOpen(false);
      onSwitch?.();
    },
    [accountLang, location, me.data, navigate, onSwitch, updateProfile],
  );

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (!ref.current) return;
      if (!ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (variant === "row") {
    return (
      <div className="flex items-center justify-between gap-2 px-3 py-2">
        <span className="text-sm text-ink-2">{t("lang.label")}</span>
        <div
          role="radiogroup"
          aria-label={t("lang.label")}
          className="inline-flex flex-wrap items-center justify-end rounded-md border border-border-strong bg-surface p-0.5"
        >
          {languages.map(({ code, label }) => {
            const active = code === lang;
            return (
              <button
                key={code}
                type="button"
                role="radio"
                aria-checked={active}
                aria-label={label}
                title={label}
                onClick={() => switchTo(code)}
                className={cx(
                  "inline-flex h-7 min-w-[2rem] items-center justify-center rounded px-1.5 text-xs uppercase tracking-wide transition-colors",
                  active
                    ? "bg-surface-3 text-ink"
                    : "text-ink-4 hover:bg-surface-2 hover:text-ink-2",
                )}
                data-testid={`lang-option-${code}`}
              >
                {code}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className="inline-flex items-center gap-1 rounded-md border border-border-strong px-2.5 py-1 text-sm uppercase tracking-wide transition hover:bg-surface-2"
        data-testid="lang-switcher-toggle"
      >
        {lang}
        <span aria-hidden className="text-[10px] text-ink-4">▾</span>
      </button>
      {open && (
        <div
          role="listbox"
          aria-label="Language"
          className="absolute right-0 z-30 mt-1 min-w-[5rem] rounded-md border border-border bg-surface p-1 shadow-md"
          data-testid="lang-switcher-menu"
        >
          {languages.map(({ code, label }) => {
            const active = code === lang;
            return (
              <button
                key={code}
                type="button"
                role="option"
                aria-selected={active}
                onClick={() => switchTo(code)}
                className="flex w-full items-center justify-between gap-3 rounded px-3 py-2 text-sm transition hover:bg-surface-2"
                data-testid={`lang-option-${code}`}
              >
                <span className={active ? "font-semibold text-ink" : "text-ink-2"}>
                  <span className="uppercase tracking-wide">{code}</span>
                  <span className="ml-1.5 text-ink-3">{label}</span>
                </span>
                {active && <span aria-hidden className="text-[10px] text-ink-3">✓</span>}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
